import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { supabase } from '../supabase/supabase';

// TEMPORARY compatibility file — thin auth stub wired to Supabase session.

const AuthContext = createContext(null);

const emptyProfile = null;

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(emptyProfile);
  const [loading, setLoading] = useState(true);

  const fetchAndSetProfile = useCallback(async (userLike) => {
    try {
      const userId = userLike?.id;
      if (!userId) {
        setUserProfile(null);
        return null;
      }

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.warn('[AuthContext TEMP] profile fetch:', error.message);
        setUserProfile(null);
        return null;
      }

      setUserProfile(data || null);
      return data || null;
    } catch (err) {
      console.warn('[AuthContext TEMP] fetchAndSetProfile failed:', err);
      setUserProfile(null);
      return null;
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) console.warn('[AuthContext TEMP] getSession:', error.message);
        const user = data?.session?.user || null;
        if (!mounted) return;
        setCurrentUser(user);
        if (user) await fetchAndSetProfile(user);
      } catch (err) {
        console.warn('[AuthContext TEMP] init failed:', err);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    const { data: listener } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        const user = session?.user || null;
        setCurrentUser(user);
        if (user) {
          await fetchAndSetProfile(user);
        } else {
          setUserProfile(null);
        }
        setLoading(false);
      }
    );

    return () => {
      mounted = false;
      listener?.subscription?.unsubscribe?.();
    };
  }, [fetchAndSetProfile]);

  const signOut = useCallback(async () => {
    try {
      await supabase.auth.signOut();
    } catch (err) {
      console.warn('[AuthContext TEMP] signOut failed:', err);
    }
    setCurrentUser(null);
    setUserProfile(null);
  }, []);

  const value = useMemo(
    () => ({
      currentUser,
      userProfile,
      loading,
      signOut,
      fetchAndSetProfile,
      setUserProfile,
    }),
    [currentUser, userProfile, loading, signOut, fetchAndSetProfile]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    return {
      currentUser: null,
      userProfile: null,
      loading: false,
      signOut: async () => {},
      fetchAndSetProfile: async () => null,
      setUserProfile: () => {},
    };
  }
  return ctx;
}

export default AuthContext;
